import { ChangeEvent, FormEvent, PointerEvent, useCallback, useEffect, useRef, useState } from "react";
import { ArrowLeft, Download, ImagePlus, Moon, Search, Sun, Type, Upload, X } from "lucide-react";
import { fetchTemplate, fetchTemplates, saveCaption, uploadTemplate } from "./api";
import type { CaptionMode, MemeTemplate } from "./types";
import { fitCanvasToContainer } from "./utils/canvas";
import { prepareUploadImage } from "./utils/images";
import { randomAlphanumericName } from "./utils/names";

type Route = { name: "gallery" } | { name: "editor"; id: string };
type LoadState = "idle" | "loading" | "ready" | "error";
type Toast = { id: number; message: string; tone: "success" | "error" | "info" };
type CaptionKey = "header" | "middle" | "bottom";
type CaptionPlacement = Record<CaptionKey, { x: number; y: number; width: number; size: number }>;
type Sticker = { id: string; src: string; x: number; y: number; width: number; aspect: number };
type DragTarget = { type: "caption"; key: CaptionKey; pointerId: number } | { type: "sticker"; id: string; pointerId: number };
type EditorDraft = {
  captions: Record<CaptionKey, string>;
  captionMode: CaptionMode;
  activeCaption: CaptionKey;
  placement: CaptionPlacement;
  stickers: Sticker[];
  activeStickerId: string | null;
};

const draftStoragePrefix = "memesdot_ke.editorDraft.";
const minCaptionFontSize = 12;

const defaultPlacement: CaptionPlacement = {
  header: { x: 0.5, y: 0.14, width: 0.82, size: 58 },
  middle: { x: 0.5, y: 0.5, width: 0.82, size: 58 },
  bottom: { x: 0.5, y: 0.86, width: 0.82, size: 58 }
};

export function App() {
  const [theme, setTheme] = useState<"light" | "dark">(() =>
    window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light"
  );
  const [route, setRoute] = useState<Route>(() => parseRoute(window.location.pathname));
  const [templates, setTemplates] = useState<MemeTemplate[]>([]);
  const [query, setQuery] = useState("");
  const [loadState, setLoadState] = useState<LoadState>("loading");
  const [toasts, setToasts] = useState<Toast[]>([]);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [online, setOnline] = useState(navigator.onLine);
  const nextToastId = useRef(0);

  const showToast = useCallback((message: string, tone: Toast["tone"] = "info") => {
    const id = ++nextToastId.current;
    setToasts((items) => [...items, { id, message, tone }].slice(-4));
  }, []);

  const dismissToast = useCallback((id: number) => {
    setToasts((items) => items.filter((item) => item.id !== id));
  }, []);

  useEffect(() => {
    document.documentElement.dataset.theme = theme;
  }, [theme]);

  useEffect(() => {
    const onPop = () => setRoute(parseRoute(window.location.pathname));
    const onOnline = () => setOnline(true);
    const onOffline = () => setOnline(false);
    window.addEventListener("popstate", onPop);
    window.addEventListener("online", onOnline);
    window.addEventListener("offline", onOffline);
    return () => {
      window.removeEventListener("popstate", onPop);
      window.removeEventListener("online", onOnline);
      window.removeEventListener("offline", onOffline);
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    fetchTemplates(query)
      .then((items) => {
        if (cancelled) return;
        setTemplates(items);
        setLoadState("ready");
      })
      .catch((error) => {
        if (cancelled) return;
        showToast(error instanceof Error ? error.message : "Could not load templates.", "error");
        setLoadState("error");
      });
    return () => {
      cancelled = true;
    };
  }, [query, showToast]);

  function navigate(next: Route) {
    const path = next.name === "gallery" ? "/" : `/edit/${next.id}`;
    window.history.pushState(null, "", path);
    setRoute(next);
    window.scrollTo({ top: 0, left: 0 });
  }

  function upsertTemplate(template: MemeTemplate) {
    setTemplates((items) => [template, ...items.filter((item) => item.id !== template.id)]);
  }

  return (
    <main className="app-shell">
      <header className="topbar">
        <button className="brand-button" onClick={() => navigate({ name: "gallery" })} type="button">
          memesdot_ke
        </button>
        <button className="icon-button" type="button" onClick={() => setTheme(theme === "dark" ? "light" : "dark")}>
          {theme === "dark" ? <Sun size={18} /> : <Moon size={18} />}
          <span className="sr-only">Toggle theme</span>
        </button>
      </header>

      {!online ? <div className="offline-banner">Offline. Loaded templates remain available.</div> : null}

      {route.name === "gallery" ? (
        <GalleryView
          loadState={loadState}
          navigate={navigate}
          query={query}
          setQuery={(nextQuery) => {
            setLoadState("loading");
            setQuery(nextQuery);
          }}
          templates={templates}
        />
      ) : (
        <EditorView key={route.id} id={route.id} initial={templates.find((template) => template.id === route.id)} navigate={navigate} />
      )}

      {route.name === "gallery" ? (
        <button className="fab" onClick={() => setUploadOpen(true)} type="button">
          <ImagePlus size={22} />
          <span className="sr-only">Upload template</span>
        </button>
      ) : null}

      <UploadSheet
        onClose={() => setUploadOpen(false)}
        onToast={showToast}
        onUploaded={(template) => {
          upsertTemplate(template);
        }}
        open={uploadOpen}
      />

      <ToastViewport dismiss={dismissToast} toasts={toasts} />
    </main>
  );
}

function GalleryView({
  loadState,
  navigate,
  query,
  setQuery,
  templates
}: {
  loadState: LoadState;
  navigate: (route: Route) => void;
  query: string;
  setQuery: (query: string) => void;
  templates: MemeTemplate[];
}) {
  return (
    <>
      <section className="search-row">
        <label className="search-field">
          <Search size={17} />
          <input
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Search labels and captions"
          />
        </label>
      </section>

      {loadState === "loading" ? <div className="loading-grid" aria-label="Loading templates" /> : null}

      <section className="gallery" aria-label="Template gallery">
        {templates.map((template) => (
          <button className="template-tile" key={template.id} onClick={() => navigate({ name: "editor", id: template.id })} type="button">
            <img alt="" loading="lazy" src={template.imageUrl} />
            {template.status === "pending" ? <span className="review-badge">In review</span> : null}
            <span className="template-label">{template.label}</span>
          </button>
        ))}
        {loadState !== "loading" && templates.length === 0 ? (
          <div className="empty-state">
            <strong>No public templates yet</strong>
            <span>Upload an image to start classification.</span>
          </div>
        ) : null}
      </section>
    </>
  );
}

function EditorView({
  id,
  initial,
  navigate
}: {
  id: string;
  initial?: MemeTemplate;
  navigate: (route: Route) => void;
}) {
  const [initialDraft] = useState(() => readEditorDraft(id));
  const [template, setTemplate] = useState<MemeTemplate | undefined>(initial);
  const [loadState, setLoadState] = useState<LoadState>(initial ? "ready" : "loading");
  const [headerText, setHeaderText] = useState(initialDraft?.captions.header ?? "");
  const [middleText, setMiddleText] = useState(initialDraft?.captions.middle ?? "");
  const [bottomText, setBottomText] = useState(initialDraft?.captions.bottom ?? "");
  const [captionMode, setCaptionMode] = useState<CaptionMode>(initialDraft?.captionMode ?? "overlay");
  const [activeCaption, setActiveCaption] = useState<CaptionKey>(initialDraft?.activeCaption ?? "bottom");
  const [placement, setPlacement] = useState<CaptionPlacement>(initialDraft?.placement ?? clonePlacement(defaultPlacement));
  const [stickers, setStickers] = useState<Sticker[]>(initialDraft?.stickers ?? []);
  const [activeStickerId, setActiveStickerId] = useState<string | null>(initialDraft?.activeStickerId ?? null);
  const [busyAction, setBusyAction] = useState<"raw" | "captioned" | null>(null);
  const [error, setError] = useState("");
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const canvasWrapRef = useRef<HTMLDivElement | null>(null);
  const stickerInputRef = useRef<HTMLInputElement | null>(null);
  const dragRef = useRef<DragTarget | null>(null);
  const renderVersionRef = useRef(0);

  useEffect(() => {
    let cancelled = false;
    if (initial) {
      return () => {
        cancelled = true;
      };
    }
    fetchTemplate(id)
      .then((item) => {
        if (cancelled) return;
        setTemplate(item);
        setLoadState("ready");
      })
      .catch((loadError) => {
        if (cancelled) return;
        setError(loadError instanceof Error ? loadError.message : "Template not found.");
        setLoadState("error");
      });
    return () => {
      cancelled = true;
    };
  }, [id, initial]);

  useEffect(() => {
    if (!template) return;
    const version = renderVersionRef.current + 1;
    renderVersionRef.current = version;
    void renderCanvas(
      canvasRef.current,
      template.imageUrl,
      { header: headerText, middle: middleText, bottom: bottomText },
      captionMode,
      placement,
      stickers,
      () => version === renderVersionRef.current
    );
  }, [template, headerText, middleText, bottomText, captionMode, placement, stickers]);

  useEffect(() => {
    const wrapper = canvasWrapRef.current;
    const canvas = canvasRef.current;
    if (!wrapper || !canvas) return;
    const resize = () => fitCanvasToContainer(canvas, wrapper);
    resize();
    const observer = new ResizeObserver(resize);
    observer.observe(wrapper);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const draft: EditorDraft = {
      captions: { header: headerText, middle: middleText, bottom: bottomText },
      captionMode,
      activeCaption,
      placement,
      stickers,
      activeStickerId
    };
    saveEditorDraft(id, draft);
  }, [id, headerText, middleText, bottomText, captionMode, activeCaption, placement, stickers, activeStickerId]);

  useEffect(() => {
    if (!template) return;
    const redraw = () => {
      const version = renderVersionRef.current + 1;
      renderVersionRef.current = version;
      void renderCanvas(
        canvasRef.current,
        template.imageUrl,
        { header: headerText, middle: middleText, bottom: bottomText },
        captionMode,
        placement,
        stickers,
        () => version === renderVersionRef.current
      );
    };
    const redrawWhenVisible = () => {
      if (!document.hidden) redraw();
    };
    window.addEventListener("pageshow", redraw);
    window.addEventListener("focus", redraw);
    document.addEventListener("visibilitychange", redrawWhenVisible);
    return () => {
      window.removeEventListener("pageshow", redraw);
      window.removeEventListener("focus", redraw);
      document.removeEventListener("visibilitychange", redrawWhenVisible);
    };
  }, [template, headerText, middleText, bottomText, captionMode, placement, stickers]);

  async function persistCaptionIfNeeded() {
    if (!template) return;
    const text = [headerText.trim(), middleText.trim(), bottomText.trim()].filter(Boolean).join(" / ");
    if (!text) return;
    await saveCaption(template.id, text, captionMode);
    setTemplate((current) => (current ? { ...current, captions: [text, ...current.captions.filter((item) => item !== text)] } : current));
  }

  async function downloadRaw() {
    if (!template) return;
    setBusyAction("raw");
    try {
      await persistCaptionIfNeeded();
      const response = await fetch(template.rawImageUrl);
      const blob = await response.blob();
      downloadBlob(blob, randomAlphanumericName("raw", "jpg"));
    } finally {
      setBusyAction(null);
    }
  }

  async function downloadCaptioned() {
    const canvas = canvasRef.current;
    if (!canvas || !template) return;
    setBusyAction("captioned");
    try {
      await persistCaptionIfNeeded();
      const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, "image/png"));
      if (blob) downloadBlob(blob, randomAlphanumericName("captioned", "png"));
    } finally {
      setBusyAction(null);
    }
  }

  function updatePlacement(key: CaptionKey, patch: Partial<CaptionPlacement[CaptionKey]>) {
    setPlacement((current) => ({
      ...current,
      [key]: nextPlacement(current[key], patch)
    }));
  }

  function captionAtPoint(x: number, y: number): CaptionKey {
    const available: CaptionKey[] = [
      ...(headerText.trim() ? (["header"] as CaptionKey[]) : []),
      ...(middleText.trim() ? (["middle"] as CaptionKey[]) : []),
      ...(bottomText.trim() ? (["bottom"] as CaptionKey[]) : [])
    ];
    if (!available.length) return activeCaption;
    return available.reduce((closest, key) => {
      const a = placement[key];
      const b = placement[closest];
      const da = Math.hypot(a.x - x, a.y - y);
      const db = Math.hypot(b.x - x, b.y - y);
      return da < db ? key : closest;
    }, available[0]!);
  }

  function canvasPoint(event: PointerEvent<HTMLCanvasElement>) {
    const canvas = canvasRef.current;
    if (!canvas) return null;
    const rect = canvas.getBoundingClientRect();
    if (!rect.width || !rect.height) return null;
    const rendered = containedCanvasRect(canvas, rect);
    return {
      x: clamp((event.clientX - rendered.left) / rendered.width, 0, 1),
      y: clamp((event.clientY - rendered.top) / rendered.height, 0, 1)
    };
  }

  function stickerAtPoint(x: number, y: number) {
    for (const sticker of [...stickers].reverse()) {
      const height = sticker.width / sticker.aspect;
      const withinX = x >= sticker.x - sticker.width / 2 && x <= sticker.x + sticker.width / 2;
      const withinY = y >= sticker.y - height / 2 && y <= sticker.y + height / 2;
      if (withinX && withinY) return sticker.id;
    }
    return null;
  }

  function handleCanvasPointerDown(event: PointerEvent<HTMLCanvasElement>) {
    const point = canvasPoint(event);
    if (!point) return;
    const stickerId = stickerAtPoint(point.x, point.y);
    if (stickerId) {
      setActiveStickerId(stickerId);
      dragRef.current = { type: "sticker", id: stickerId, pointerId: event.pointerId };
      event.currentTarget.setPointerCapture(event.pointerId);
      return;
    }
    if (captionMode !== "overlay") return;
    const key = captionAtPoint(point.x, point.y);
    setActiveCaption(key);
    setActiveStickerId(null);
    updatePlacement(key, point);
    dragRef.current = { type: "caption", key, pointerId: event.pointerId };
    event.currentTarget.setPointerCapture(event.pointerId);
  }

  function handleCanvasPointerMove(event: PointerEvent<HTMLCanvasElement>) {
    const drag = dragRef.current;
    if (!drag || drag.pointerId !== event.pointerId) return;
    const point = canvasPoint(event);
    if (!point) return;
    if (drag.type === "caption") {
      updatePlacement(drag.key, point);
      return;
    }
    setStickers((current) => current.map((sticker) => (sticker.id === drag.id ? { ...sticker, x: point.x, y: point.y } : sticker)));
  }

  function handleCanvasPointerUp(event: PointerEvent<HTMLCanvasElement>) {
    const drag = dragRef.current;
    if (!drag || drag.pointerId !== event.pointerId) return;
    dragRef.current = null;
    event.currentTarget.releasePointerCapture(event.pointerId);
  }

  async function handleStickerUpload(event: ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    const src = await fileToDataUrl(file);
    const image = await loadImage(src);
    const aspect = Math.max(0.2, (image.naturalWidth || image.width) / Math.max(1, image.naturalHeight || image.height));
    const id = crypto.randomUUID();
    setStickers((current) => [...current, { id, src, x: 0.5, y: 0.5, width: 0.22, aspect }]);
    setActiveStickerId(id);
  }

  function updateActiveStickerWidth(width: number) {
    if (!activeStickerId) return;
    setStickers((current) => current.map((sticker) => (sticker.id === activeStickerId ? { ...sticker, width } : sticker)));
  }

  function blurActiveInput() {
    const activeElement = document.activeElement;
    if (activeElement instanceof HTMLElement) activeElement.blur();
  }

  function removeActiveSticker() {
    if (!activeStickerId) return;
    setStickers((current) => current.filter((sticker) => sticker.id !== activeStickerId));
    setActiveStickerId(null);
  }

  function clearDraft() {
    if (!window.confirm("Clear this draft?")) return;
    clearEditorDraft(id);
    setHeaderText("");
    setMiddleText("");
    setBottomText("");
    setCaptionMode("overlay");
    setActiveCaption("bottom");
    setPlacement(clonePlacement(defaultPlacement));
    setStickers([]);
    setActiveStickerId(null);
  }

  if (loadState === "loading") {
    return <div className="editor-loading">Loading editor</div>;
  }

  if (!template) {
    return (
      <section className="empty-state">
        <strong>{error || "Template not found"}</strong>
        <button className="secondary-button" onClick={() => navigate({ name: "gallery" })} type="button">
          <ArrowLeft size={17} />
          Gallery
        </button>
      </section>
    );
  }

  return (
    <section className="editor-route" aria-label="Caption editor">
      <div className="editor-header">
        <button className="secondary-button" onClick={() => navigate({ name: "gallery" })} type="button">
          <ArrowLeft size={17} />
          Gallery
        </button>
        <strong className="editor-label">{template.label}</strong>
      </div>

      <div className="editor-workspace">
        <div className="editor-preview">
          <div className="canvas-wrap" ref={canvasWrapRef}>
            <canvas
              ref={canvasRef}
              width={900}
              height={650}
              onPointerDown={handleCanvasPointerDown}
              onPointerMove={handleCanvasPointerMove}
              onPointerUp={handleCanvasPointerUp}
              onPointerCancel={handleCanvasPointerUp}
            />
          </div>
        </div>

        <div className="editor-tools">
          <div className="caption-stack">
            <div className="segmented" aria-label="Caption placement">
              <button className={captionMode === "overlay" ? "active" : ""} onClick={() => setCaptionMode("overlay")} type="button">
                Overlay
              </button>
              <button className={captionMode === "top" ? "active" : ""} onClick={() => setCaptionMode("top")} type="button">
                Top
              </button>
            </div>
            <label className="caption-input">
              <Type size={17} />
              <input
                maxLength={140}
                onFocus={() => setActiveCaption("header")}
                onChange={(event) => setHeaderText(event.target.value)}
                placeholder="Header text"
                value={headerText}
              />
            </label>
            <label className="caption-input">
              <Type size={17} />
              <input
                maxLength={140}
                onFocus={() => setActiveCaption("middle")}
                onChange={(event) => setMiddleText(event.target.value)}
                placeholder="Middle text"
                value={middleText}
              />
            </label>
            <label className="caption-input">
              <Type size={17} />
              <input
                maxLength={140}
                onFocus={() => setActiveCaption("bottom")}
                onChange={(event) => setBottomText(event.target.value)}
                placeholder="Bottom text"
                value={bottomText}
              />
            </label>
            {captionMode === "overlay" ? (
              <div className="placement-panel">
                <div className="segmented compact" aria-label="Active caption">
                  <button className={activeCaption === "header" ? "active" : ""} onClick={() => setActiveCaption("header")} type="button">
                    Header
                  </button>
                  <button className={activeCaption === "middle" ? "active" : ""} onClick={() => setActiveCaption("middle")} type="button">
                    Middle
                  </button>
                  <button className={activeCaption === "bottom" ? "active" : ""} onClick={() => setActiveCaption("bottom")} type="button">
                    Bottom
                  </button>
                </div>
                <label>
                  <span>Size</span>
                  <input
                    max={92}
                    min={minCaptionFontSize}
                    onChange={(event) => updatePlacement(activeCaption, { size: Number(event.target.value) })}
                    onPointerDown={blurActiveInput}
                    onTouchStart={blurActiveInput}
                    type="range"
                    value={placement[activeCaption].size}
                  />
                </label>
                <label>
                  <span>Width</span>
                  <input
                    max={96}
                    min={18}
                    onChange={(event) => updatePlacement(activeCaption, { width: Number(event.target.value) / 100 })}
                    onPointerDown={blurActiveInput}
                    onTouchStart={blurActiveInput}
                    type="range"
                    value={Math.round(placement[activeCaption].width * 100)}
                  />
                </label>
              </div>
            ) : null}
            <div className="sticker-panel">
              <input
                ref={stickerInputRef}
                accept="image/jpeg,image/png,image/webp"
                className="sr-only"
                onChange={handleStickerUpload}
                type="file"
              />
              <button className="secondary-button" onClick={() => stickerInputRef.current?.click()} type="button">
                <ImagePlus size={17} />
                Sticker
              </button>
              {activeStickerId ? (
                <>
                  <label>
                    <span>Sticker size</span>
                    <input
                      max={65}
                      min={8}
                      onChange={(event) => updateActiveStickerWidth(Number(event.target.value) / 100)}
                      onPointerDown={blurActiveInput}
                      onTouchStart={blurActiveInput}
                      type="range"
                      value={Math.round((stickers.find((sticker) => sticker.id === activeStickerId)?.width ?? 0.22) * 100)}
                    />
                  </label>
                  <button className="secondary-button" onClick={removeActiveSticker} type="button">
                    Remove
                  </button>
                </>
              ) : null}
            </div>
          </div>

          <div className="action-row">
            <button className="secondary-button" disabled={busyAction !== null} onClick={downloadRaw} type="button">
              <Download size={17} />
              {busyAction === "raw" ? "Saving" : "Raw"}
            </button>
            <button className="primary-button" disabled={busyAction !== null} onClick={downloadCaptioned} type="button">
              <Download size={17} />
              {busyAction === "captioned" ? "Saving" : "Captioned"}
            </button>
            <button className="secondary-button" disabled={busyAction !== null} onClick={clearDraft} type="button">
              <X size={17} />
              Clear draft
            </button>
          </div>

          {template.captions.length > 0 ? (
            <div className="saved-captions">
              {template.captions.slice(0, 6).map((item) => (
                <button key={item} onClick={() => setBottomText(item)} type="button">
                  {item}
                </button>
              ))}
            </div>
          ) : null}
        </div>
      </div>
    </section>
  );
}

function UploadSheet({
  onClose,
  onToast,
  onUploaded,
  open
}: {
  onClose: () => void;
  onToast: (message: string, tone?: Toast["tone"]) => void;
  onUploaded: (template: MemeTemplate) => void;
  open: boolean;
}) {
  const [state, setState] = useState<"idle" | "preparing" | "uploading">("idle");
  const [progress, setProgress] = useState({ current: 0, total: 0 });

  async function handleUpload(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = event.currentTarget;
    const input = form.elements.namedItem("image");
    if (!(input instanceof HTMLInputElement) || !input.files?.length) return;
    const files = Array.from(input.files);
    let uploaded = 0;
    const failures: string[] = [];
    setProgress({ current: 1, total: files.length });

    for (const [index, file] of files.entries()) {
      try {
        setProgress({ current: index + 1, total: files.length });
        setState("preparing");
        const prepared = await prepareUploadImage(file);
        const upload = new FormData();
        upload.set("image", prepared.original, "template.jpg");
        upload.set("viewImage", prepared.view, "template-view.jpg");
        upload.set("width", String(prepared.width));
        upload.set("height", String(prepared.height));
        setState("uploading");
        const template = await uploadTemplate(upload);
        onUploaded(template);
        uploaded += 1;
      } catch (error) {
        const reason = error instanceof Error ? error.message : "Upload failed.";
        failures.push(`${file.name}: ${reason}`);
      }
    }

    setState("idle");
    setProgress({ current: 0, total: 0 });
    if (uploaded > 0) {
      onToast(`${uploaded} ${uploaded === 1 ? "template" : "templates"} uploaded for review.`, "success");
    }
    if (failures.length > 0) {
      onToast(`${failures.length} failed — ${failures.slice(0, 2).join("; ")}`, "error");
    }
    form.reset();
    if (uploaded > 0) onClose();
  }

  if (!open) return null;

  return (
    <div className="sheet-backdrop" role="presentation">
      <form className="bottom-sheet" onSubmit={handleUpload}>
        <div className="sheet-header">
          <strong>Upload images</strong>
          <button className="icon-button" disabled={state !== "idle"} onClick={onClose} type="button">
            <X size={18} />
            <span className="sr-only">Close upload</span>
          </button>
        </div>
        <label>
          <span>Images</span>
          <input className="file-input" accept="image/jpeg,image/png,image/webp" multiple name="image" required type="file" />
        </label>
        <span className="upload-hint">Choose one or more JPEG, PNG, or WebP images.</span>
        <button className="primary-button" disabled={state !== "idle"} type="submit">
          <Upload size={17} />
          {state === "preparing"
            ? `Preparing ${progress.current} of ${progress.total}`
            : state === "uploading"
              ? `Uploading ${progress.current} of ${progress.total}`
              : "Upload images"}
        </button>
      </form>
    </div>
  );
}

function ToastViewport({ dismiss, toasts }: { dismiss: (id: number) => void; toasts: Toast[] }) {
  return (
    <div className="toast-viewport" aria-live="polite" aria-label="Notifications">
      {toasts.map((toast) => (
        <ToastItem dismiss={dismiss} key={toast.id} toast={toast} />
      ))}
    </div>
  );
}

function ToastItem({ dismiss, toast }: { dismiss: (id: number) => void; toast: Toast }) {
  useEffect(() => {
    const timeout = window.setTimeout(() => dismiss(toast.id), toast.tone === "error" ? 6500 : 4000);
    return () => window.clearTimeout(timeout);
  }, [dismiss, toast.id, toast.tone]);

  return (
    <div className={`toast toast-${toast.tone}`} role={toast.tone === "error" ? "alert" : "status"}>
      <span>{toast.message}</span>
      <button aria-label="Dismiss notification" onClick={() => dismiss(toast.id)} type="button">
        <X size={16} />
      </button>
    </div>
  );
}

function parseRoute(pathname: string): Route {
  const match = pathname.match(/^\/edit\/([^/]+)$/);
  return match ? { name: "editor", id: decodeURIComponent(match[1]) } : { name: "gallery" };
}

function draftStorageKey(templateId: string) {
  return `${draftStoragePrefix}${templateId}`;
}

function readEditorDraft(templateId: string): EditorDraft | null {
  try {
    const raw = localStorage.getItem(draftStorageKey(templateId));
    if (!raw) return null;
    return normalizeEditorDraft(JSON.parse(raw));
  } catch {
    return null;
  }
}

function saveEditorDraft(templateId: string, draft: EditorDraft) {
  try {
    if (!hasDraftContent(draft)) {
      clearEditorDraft(templateId);
      return;
    }
    localStorage.setItem(draftStorageKey(templateId), JSON.stringify(draft));
  } catch {
    // Ignore storage quota or privacy-mode errors; the editor still works without draft persistence.
  }
}

function clearEditorDraft(templateId: string) {
  try {
    localStorage.removeItem(draftStorageKey(templateId));
  } catch {
    // Ignore storage errors.
  }
}

function hasDraftContent(draft: EditorDraft) {
  const hasCaption = Object.values(draft.captions).some((text) => text.trim());
  const hasCustomPlacement = (Object.keys(defaultPlacement) as CaptionKey[]).some((key) => {
    const current = draft.placement[key];
    const initial = defaultPlacement[key];
    return current.x !== initial.x || current.y !== initial.y || current.width !== initial.width || current.size !== initial.size;
  });
  return hasCaption || draft.captionMode !== "overlay" || hasCustomPlacement || draft.stickers.length > 0;
}

function normalizeEditorDraft(value: unknown): EditorDraft | null {
  if (!isObject(value)) return null;
  const captions = isObject(value.captions) ? value.captions : {};
  const placement = isObject(value.placement) ? value.placement : {};
  const stickers = Array.isArray(value.stickers) ? value.stickers.map(normalizeSticker).filter((sticker) => sticker !== null) : [];
  const activeStickerId = typeof value.activeStickerId === "string" && stickers.some((sticker) => sticker.id === value.activeStickerId) ? value.activeStickerId : null;
  return {
    captions: {
      header: typeof captions.header === "string" ? captions.header : "",
      middle: typeof captions.middle === "string" ? captions.middle : "",
      bottom: typeof captions.bottom === "string" ? captions.bottom : ""
    },
    captionMode: value.captionMode === "top" ? "top" : "overlay",
    activeCaption: isCaptionKey(value.activeCaption) ? value.activeCaption : "bottom",
    placement: {
      header: normalizePlacement(placement.header, defaultPlacement.header),
      middle: normalizePlacement(placement.middle, defaultPlacement.middle),
      bottom: normalizePlacement(placement.bottom, defaultPlacement.bottom)
    },
    stickers,
    activeStickerId
  };
}

function normalizePlacement(value: unknown, fallback: CaptionPlacement[CaptionKey]): CaptionPlacement[CaptionKey] {
  if (!isObject(value)) return { ...fallback };
  return nextPlacement(fallback, {
    x: typeof value.x === "number" ? value.x : fallback.x,
    y: typeof value.y === "number" ? value.y : fallback.y,
    width: typeof value.width === "number" ? value.width : fallback.width,
    size: typeof value.size === "number" ? value.size : fallback.size
  });
}

function normalizeSticker(value: unknown): Sticker | null {
  if (!isObject(value)) return null;
  if (typeof value.id !== "string" || typeof value.src !== "string") return null;
  if (!value.src.startsWith("data:image/")) return null;
  return {
    id: value.id,
    src: value.src,
    x: typeof value.x === "number" ? clamp(value.x, 0, 1) : 0.5,
    y: typeof value.y === "number" ? clamp(value.y, 0, 1) : 0.5,
    width: typeof value.width === "number" ? clamp(value.width, 0.08, 0.65) : 0.22,
    aspect: typeof value.aspect === "number" ? Math.max(0.2, value.aspect) : 1
  };
}

function clonePlacement(placement: CaptionPlacement): CaptionPlacement {
  return {
    header: { ...placement.header },
    middle: { ...placement.middle },
    bottom: { ...placement.bottom }
  };
}

function isCaptionKey(value: unknown): value is CaptionKey {
  return value === "header" || value === "middle" || value === "bottom";
}

function isObject(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

function containedCanvasRect(canvas: HTMLCanvasElement, rect: DOMRect) {
  const canvasAspect = canvas.width / Math.max(1, canvas.height);
  const boxAspect = rect.width / Math.max(1, rect.height);
  if (boxAspect > canvasAspect) {
    const width = rect.height * canvasAspect;
    return {
      left: rect.left + (rect.width - width) / 2,
      top: rect.top,
      width,
      height: rect.height
    };
  }
  const height = rect.width / canvasAspect;
  return {
    left: rect.left,
    top: rect.top + (rect.height - height) / 2,
    width: rect.width,
    height
  };
}

async function renderCanvas(
  canvas: HTMLCanvasElement | null,
  imageUrl: string,
  captions: Record<CaptionKey, string>,
  mode: CaptionMode,
  placement: CaptionPlacement = defaultPlacement,
  stickers: Sticker[] = [],
  isCurrent: () => boolean = () => true
) {
  if (!canvas) return;
  const context = canvas.getContext("2d");
  if (!context) return;
  const image = await loadImage(imageUrl);
  if (!isCurrent()) return;
  const { width: imageWidth, height: imageHeight } = scaledImageSize(image, 1200);
  const bandHeight = 128;
  const headerText = captions.header;
  const middleText = captions.middle;
  const bottomText = captions.bottom;
  const headerBand = mode === "top" && headerText.trim() ? bandHeight : 0;
  const bottomBand = mode === "top" && bottomText.trim() ? bandHeight : 0;
  canvas.width = imageWidth;
  canvas.height = imageHeight + headerBand + bottomBand;
  const wrapper = canvas.parentElement;
  if (wrapper) fitCanvasToContainer(canvas, wrapper);
  context.fillStyle = "#ffffff";
  context.fillRect(0, 0, canvas.width, canvas.height);
  context.drawImage(image, 0, headerBand, imageWidth, imageHeight);
  if (headerText.trim()) {
    const headerPlacement = placement.header;
    drawCaption(
      context,
      headerText.trim(),
      mode === "top" ? canvas.width / 2 : headerPlacement.x * canvas.width,
      mode === "top" ? headerBand / 2 : headerPlacement.y * canvas.height,
      mode,
      imageWidth,
      mode === "top" ? headerBand : undefined,
      mode === "overlay" ? headerPlacement : undefined
    );
  }
  if (middleText.trim()) {
    const middlePlacement = placement.middle;
    drawCaption(
      context,
      middleText.trim(),
      middlePlacement.x * canvas.width,
      mode === "top" ? headerBand + middlePlacement.y * imageHeight : middlePlacement.y * canvas.height,
      "overlay",
      imageWidth,
      undefined,
      middlePlacement
    );
  }
  if (bottomText.trim()) {
    const bottomPlacement = placement.bottom;
    drawCaption(
      context,
      bottomText.trim(),
      mode === "top" ? canvas.width / 2 : bottomPlacement.x * canvas.width,
      mode === "top" ? headerBand + imageHeight + bottomBand / 2 : bottomPlacement.y * canvas.height,
      mode,
      imageWidth,
      mode === "top" ? bottomBand : undefined,
      mode === "overlay" ? bottomPlacement : undefined
    );
  }
  await drawStickers(context, stickers, canvas.width, canvas.height, isCurrent);
}

function scaledImageSize(image: HTMLImageElement, maxSide: number) {
  const sourceWidth = image.naturalWidth || image.width;
  const sourceHeight = image.naturalHeight || image.height;
  const scale = Math.min(1, maxSide / Math.max(sourceWidth, sourceHeight));
  return {
    width: Math.max(1, Math.round(sourceWidth * scale)),
    height: Math.max(1, Math.round(sourceHeight * scale))
  };
}

function drawCaption(
  context: CanvasRenderingContext2D,
  text: string,
  centerX: number,
  y: number,
  mode: CaptionMode,
  canvasWidth: number,
  bandHeight?: number,
  placement?: CaptionPlacement[CaptionKey]
) {
  const maxWidth = Math.max(180, Math.min(canvasWidth - 32, (placement?.width ?? 1) * canvasWidth));
  const maxLines = mode === "top" ? 2 : 3;
  const maxBlockHeight = mode === "top" ? Math.max(72, (bandHeight ?? 128) - 24) : 420;
  let fontSize = placement?.size ?? Math.max(28, Math.min(60, Math.round(canvasWidth * 0.064)));
  const family = "Impact, Arial Black, sans-serif";
  let lines: string[] = [];
  let lineHeight = 0;
  do {
    context.font = `${fontSize}px ${family}`;
    lineHeight = Math.round(fontSize * 1.08);
    lines = clampLines(wrapText(context, text.toUpperCase(), maxWidth), maxLines);
    fontSize -= 2;
  } while (
    (lines.length > maxLines ||
      lines.length * lineHeight > maxBlockHeight ||
      lines.some((line) => context.measureText(line).width > maxWidth)) &&
    fontSize > minCaptionFontSize
  );
  fontSize += 2;
  context.font = `${fontSize}px ${family}`;
  lineHeight = Math.round(fontSize * 1.08);

  context.textAlign = "center";
  context.textBaseline = "middle";
  context.lineJoin = "round";
  lines.forEach((line, index) => {
    const lineY = y + (index - (lines.length - 1) / 2) * lineHeight;
    context.lineWidth = mode === "top" ? 0 : 12;
    if (mode === "overlay") {
      context.strokeStyle = "#000000";
      context.strokeText(line, centerX, lineY);
    }
    context.fillStyle = mode === "top" ? "#111111" : "#ffffff";
    context.fillText(line, centerX, lineY);
  });
}

async function drawStickers(
  context: CanvasRenderingContext2D,
  stickers: Sticker[],
  canvasWidth: number,
  canvasHeight: number,
  isCurrent: () => boolean
) {
  for (const sticker of stickers) {
    const image = await loadImage(sticker.src);
    if (!isCurrent()) return;
    const width = sticker.width * canvasWidth;
    const height = width / sticker.aspect;
    context.drawImage(image, sticker.x * canvasWidth - width / 2, sticker.y * canvasHeight - height / 2, width, height);
  }
}

function wrapText(context: CanvasRenderingContext2D, text: string, maxWidth: number) {
  const words = text.split(/\s+/);
  const lines: string[] = [];
  let current = "";
  for (const word of words) {
    const chunks = splitLongWord(context, word, maxWidth);
    for (const chunk of chunks) {
      const next = current ? `${current} ${chunk}` : chunk;
      if (context.measureText(next).width <= maxWidth || !current) {
        current = next;
      } else {
        lines.push(current);
        current = chunk;
      }
    }
  }
  if (current) lines.push(current);
  return lines;
}

function splitLongWord(context: CanvasRenderingContext2D, word: string, maxWidth: number) {
  if (context.measureText(word).width <= maxWidth) return [word];
  const chunks: string[] = [];
  let current = "";
  for (const char of word) {
    const next = `${current}${char}`;
    if (context.measureText(next).width <= maxWidth || !current) {
      current = next;
    } else {
      chunks.push(current);
      current = char;
    }
  }
  if (current) chunks.push(current);
  return chunks;
}

function clampLines(lines: string[], maxLines: number) {
  if (lines.length <= maxLines) return lines;
  const visible = lines.slice(0, maxLines);
  visible[maxLines - 1] = `${visible[maxLines - 1].replace(/\.+$/, "")}...`;
  return visible;
}

function clamp(value: number, min: number, max: number) {
  return Math.max(min, Math.min(max, value));
}

function nextPlacement(
  current: CaptionPlacement[CaptionKey],
  patch: Partial<CaptionPlacement[CaptionKey]>
): CaptionPlacement[CaptionKey] {
  const width = clamp(patch.width ?? current.width, 0.18, 0.96);
  return {
    x: clamp(patch.x ?? current.x, 0, 1),
    y: clamp(patch.y ?? current.y, 0, 1),
    width,
    size: clamp(patch.size ?? current.size, 24, 92)
  };
}

function fileToDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      if (typeof reader.result === "string") resolve(reader.result);
      else reject(new Error("Sticker image could not be loaded."));
    };
    reader.onerror = () => reject(new Error("Sticker image could not be loaded."));
    reader.readAsDataURL(file);
  });
}

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.crossOrigin = "anonymous";
    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error("Image failed to load."));
    image.src = src;
  });
}

function downloadBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
}
